import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { students } from '../api/client';
import ProgressChart from '../components/charts/ProgressChart';
import SkillMasteryChart from '../components/charts/SkillMasteryChart';

const MASTERY_COLORS = {
  mastered: '#10B981',
  developing: '#3B82F6',
  emerging: '#F59E0B',
  not_demonstrated: '#EF4444',
};

const MASTERY_LABELS = {
  mastered: 'Mastered',
  developing: 'Developing',
  emerging: 'Emerging',
  not_demonstrated: 'Not Demonstrated',
};

export default function StudentProgressPage() {
  const { studentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    loadProgress();
  }, [studentId]);

  const loadProgress = async () => {
    try {
      setLoading(true);
      const response = await students.progress(studentId);
      setData(response.data);
    } catch (err) {
      console.error('Failed to load progress:', err);
      setError('Failed to load student progress');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading progress data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-gray-500">No data available</div>
      </div>
    );
  }

  const { student, assessments, current_skills, current_gaps, recently_mastered, skill_progression } = data;

  // Get unique categories for filter
  const categories = [...new Set(current_skills.map(s => s.category))].sort();

  // Filter skills by category
  const filteredSkills = categoryFilter === 'all'
    ? current_skills
    : current_skills.filter(s => s.category === categoryFilter);

  return (
    <div className="px-4 py-6 sm:px-0">
      {/* Header */}
      <div className="mb-4">
        <Link
          to={`/app/students/${studentId}`}
          className="text-sm text-indigo-600 hover:text-indigo-500"
        >
          &larr; Back to {student.full_name}
        </Link>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          Progress Dashboard
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {student.full_name}
          {student.grade_level ? ` | Grade ${student.grade_level}` : ''}
        </p>
      </div>

      {/* No assessments message */}
      {assessments.length === 0 ? (
        <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No assessment data yet</h3>
          <p className="mt-1 text-sm text-gray-500">
            Complete assessments to start tracking progress.
          </p>
          <Link
            to={`/app/students/${studentId}/assessments`}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Start an Assessment
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Progress Over Time Chart */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Assessment Scores Over Time</h2>
            <ProgressChart data={assessments} height={300} />
          </div>

          {/* Two column layout for skills */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Current Skill Mastery */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">Current Skill Mastery</h2>
                {categories.length > 1 && (
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="text-sm border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    <option value="all">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat.toUpperCase()}</option>
                    ))}
                  </select>
                )}
              </div>
              <SkillMasteryChart data={filteredSkills} height={Math.max(300, filteredSkills.length * 25)} />
            </div>

            {/* Learning Gaps */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Learning Gaps
                {current_gaps.length > 0 && (
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({current_gaps.length} skills need attention)
                  </span>
                )}
              </h2>
              {current_gaps.length === 0 ? (
                <div className="flex items-center justify-center h-48 bg-green-50 rounded-lg">
                  <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="mt-2 text-sm text-green-700">No significant gaps identified</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {current_gaps.map((skill, index) => (
                    <div
                      key={skill.skill_code}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {skill.skill_name}
                        </p>
                        <p className="text-xs text-gray-500">{skill.skill_code}</p>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-sm font-medium" style={{ color: MASTERY_COLORS[skill.mastery_level] }}>
                          {Math.round(skill.percentage)}%
                        </span>
                        <span
                          className="px-2 py-1 text-xs font-medium rounded-full"
                          style={{
                            backgroundColor: `${MASTERY_COLORS[skill.mastery_level]}20`,
                            color: MASTERY_COLORS[skill.mastery_level]
                          }}
                        >
                          {MASTERY_LABELS[skill.mastery_level]}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recently Mastered Skills */}
          {recently_mastered.length > 0 && (
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Recently Mastered Skills
                <span className="ml-2 text-sm font-normal text-green-600">
                  ({recently_mastered.length} skills)
                </span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {recently_mastered.map((skill) => (
                  <div
                    key={skill.skill_code}
                    className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200"
                  >
                    <svg className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {skill.skill_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        Was: {MASTERY_LABELS[skill.previous_level]}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Assessment History Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Assessment History</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assessment
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Benchmark
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {assessments.slice().reverse().map((assessment) => (
                    <tr key={assessment.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {assessment.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(assessment.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {Math.round(assessment.percentage)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <BenchmarkBadge level={assessment.benchmark_level} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <Link
                          to={`/app/assessments/${assessment.id}/results`}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          View Results
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BenchmarkBadge({ level }) {
  const styles = {
    exceeding: 'bg-green-100 text-green-800',
    meeting: 'bg-blue-100 text-blue-800',
    approaching: 'bg-yellow-100 text-yellow-800',
    below: 'bg-red-100 text-red-800',
  };
  const labels = {
    exceeding: 'Exceeding',
    meeting: 'Meeting',
    approaching: 'Approaching',
    below: 'Below',
  };

  if (!level) return null;

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[level] || 'bg-gray-100 text-gray-800'}`}>
      {labels[level] || level}
    </span>
  );
}
